import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  CandidateView, HireReadinessView, HireResultView, InterviewFeedbackView, InterviewView,
  JobApplicationView, JobPostingView, JobRequisitionView, OfferView, RecruiterDashboardView,
  RecruitmentBoardView, RecruitmentSettingView, RecruitmentSourceView, RejectionReasonView,
} from '../types'

const REQ = '/v1/requisitions'
const CAND = '/v1/candidates'
const OFF = '/v1/offers'
const CFG = '/v1/recruitment'

export function useRequisitions() {
  return useQuery({
    queryKey: ['recruitment', 'requisitions'],
    queryFn: async () => (await apiClient.get<JobRequisitionView[]>(REQ)).data,
  })
}

export function useRequisition(id: string | undefined) {
  return useQuery({
    queryKey: ['recruitment', 'requisitions', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<JobRequisitionView>(`${REQ}/${id}`)).data,
  })
}

export function useRecruitmentBoard(requisitionId: string | null) {
  return useQuery({
    queryKey: ['recruitment', 'board', requisitionId],
    enabled: Boolean(requisitionId),
    queryFn: async () =>
      (await apiClient.get<RecruitmentBoardView>(`${REQ}/${requisitionId}/board`)).data,
  })
}

export function useRequisitionMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['recruitment'] })
  }

  return {
    raise: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<JobRequisitionView>(REQ, body)).data,
      onSuccess: invalidate,
    }),
    addStage: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<JobRequisitionView>(`${REQ}/${id}/stages`, body)).data,
      onSuccess: invalidate,
    }),
    submit: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<JobRequisitionView>(`${REQ}/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    decide: useMutation({
      mutationFn: async ({ id, approved, comment }: {
        id: string; approved: boolean; comment?: string
      }) => (await apiClient.post<JobRequisitionView>(
        `${REQ}/${id}/decision`, { approved, comment })).data,
      onSuccess: invalidate,
    }),
    close: useMutation({
      mutationFn: async ({ id, status, reason }: {
        id: string; status: string; reason?: string
      }) => (await apiClient.post<JobRequisitionView>(
        `${REQ}/${id}/close`, { status, reason })).data,
      onSuccess: invalidate,
    }),
  }
}

export function useCandidates() {
  return useQuery({
    queryKey: ['recruitment', 'candidates'],
    queryFn: async () => (await apiClient.get<CandidateView[]>(CAND)).data,
  })
}

export function useCandidate(id: string | undefined) {
  return useQuery({
    queryKey: ['recruitment', 'candidates', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<CandidateView>(`${CAND}/${id}`)).data,
  })
}

export function useApplications() {
  return useQuery({
    queryKey: ['recruitment', 'applications'],
    queryFn: async () => (await apiClient.get<JobApplicationView[]>(`${CAND}/applications`)).data,
  })
}

export function useInterviews(applicationId: string | null) {
  return useQuery({
    queryKey: ['recruitment', 'applications', applicationId, 'interviews'],
    enabled: Boolean(applicationId),
    queryFn: async () =>
      (await apiClient.get<InterviewView[]>(
        `${CAND}/applications/${applicationId}/interviews`)).data,
  })
}

export function useHireReadiness(applicationId: string | null) {
  return useQuery({
    queryKey: ['recruitment', 'applications', applicationId, 'hire-readiness'],
    enabled: Boolean(applicationId),
    queryFn: async () =>
      (await apiClient.get<HireReadinessView>(
        `${CAND}/applications/${applicationId}/hire-readiness`)).data,
  })
}

export function useCandidateMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['recruitment'] })
    void queryClient.invalidateQueries({ queryKey: ['employees'] })
  }

  return {
    save: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<CandidateView>(CAND, body)).data,
      onSuccess: invalidate,
    }),
    apply: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<JobApplicationView>(`${CAND}/applications`, body)).data,
      onSuccess: invalidate,
    }),
    moveStage: useMutation({
      mutationFn: async ({ id, stageId }: { id: string; stageId: string }) =>
        (await apiClient.post<JobApplicationView>(
          `${CAND}/applications/${id}/stage`, { stageId })).data,
      onSuccess: invalidate,
    }),
    reject: useMutation({
      mutationFn: async ({ id, rejectionReasonId, comment }: {
        id: string; rejectionReasonId: string; comment?: string
      }) => (await apiClient.post<JobApplicationView>(
        `${CAND}/applications/${id}/reject`, { rejectionReasonId, comment })).data,
      onSuccess: invalidate,
    }),
    schedule: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<InterviewView>(
          `${CAND}/applications/${id}/interviews`, body)).data,
      onSuccess: invalidate,
    }),
    submitFeedback: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<InterviewFeedbackView>(
          `${CAND}/interviews/${id}/feedback`, body)).data,
      onSuccess: invalidate,
    }),
    completeInterview: useMutation({
      mutationFn: async ({ id, outcome, notes }: {
        id: string; outcome?: string; notes?: string
      }) => (await apiClient.post<InterviewView>(
        `${CAND}/interviews/${id}/complete`, { outcome, notes })).data,
      onSuccess: invalidate,
    }),
    hire: useMutation({
      mutationFn: async ({ id, body }: { id: string; body?: Record<string, unknown> }) =>
        (await apiClient.post<HireResultView>(
          `${CAND}/applications/${id}/hire`, body ?? {})).data,
      onSuccess: invalidate,
    }),
  }
}

export function useOffers() {
  return useQuery({
    queryKey: ['recruitment', 'offers'],
    queryFn: async () => (await apiClient.get<OfferView[]>(OFF)).data,
  })
}

export function useOfferMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['recruitment'] })
  }

  return {
    prepare: useMutation({
      mutationFn: async ({ applicationId, body }: {
        applicationId: string; body: Record<string, unknown>
      }) => (await apiClient.post<OfferView>(`${OFF}/applications/${applicationId}`, body)).data,
      onSuccess: invalidate,
    }),
    submit: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<OfferView>(`${OFF}/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    approve: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<OfferView>(`${OFF}/${id}/approve`)).data,
      onSuccess: invalidate,
    }),
    send: useMutation({
      mutationFn: async ({ id, validUntil }: { id: string; validUntil?: string }) =>
        (await apiClient.post<OfferView>(`${OFF}/${id}/send`, { validUntil })).data,
      onSuccess: invalidate,
    }),
    respond: useMutation({
      mutationFn: async ({ id, accepted, declineReason }: {
        id: string; accepted: boolean; declineReason?: string
      }) => (await apiClient.post<OfferView>(
        `${OFF}/${id}/response`, { accepted, declineReason })).data,
      onSuccess: invalidate,
    }),
    withdraw: useMutation({
      mutationFn: async ({ id, declineReason }: { id: string; declineReason?: string }) =>
        (await apiClient.post<OfferView>(
          `${OFF}/${id}/withdraw`, { accepted: false, declineReason })).data,
      onSuccess: invalidate,
    }),
  }
}

export function useRecruitmentSources() {
  return useQuery({
    queryKey: ['recruitment', 'sources'],
    queryFn: async () => (await apiClient.get<RecruitmentSourceView[]>(`${CFG}/sources`)).data,
  })
}

export function useRejectionReasons() {
  return useQuery({
    queryKey: ['recruitment', 'rejection-reasons'],
    queryFn: async () =>
      (await apiClient.get<RejectionReasonView[]>(`${CFG}/rejection-reasons`)).data,
  })
}

export function useJobPostings() {
  return useQuery({
    queryKey: ['recruitment', 'postings'],
    queryFn: async () => (await apiClient.get<JobPostingView[]>(`${CFG}/postings`)).data,
  })
}

export function useRecruitmentSettings() {
  return useQuery({
    queryKey: ['recruitment', 'settings'],
    queryFn: async () =>
      (await apiClient.get<RecruitmentSettingView[]>(`${CFG}/settings`)).data,
  })
}

export function useRecruiterDashboard() {
  return useQuery({
    queryKey: ['recruitment', 'dashboard'],
    queryFn: async () =>
      (await apiClient.get<RecruiterDashboardView>(`${CFG}/dashboard`)).data,
  })
}

export function useRecruitmentConfigMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['recruitment'] })
  }

  return {
    saveSource: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<RecruitmentSourceView>(`${CFG}/sources/${id}`, body)).data
          : (await apiClient.post<RecruitmentSourceView>(`${CFG}/sources`, body)).data,
      onSuccess: invalidate,
    }),
    /** Retires it: no longer offered, still resolving everywhere it is already named. */
    retireSource: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<RecruitmentSourceView>(`${CFG}/sources/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    saveReason: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<RejectionReasonView>(`${CFG}/rejection-reasons/${id}`, body)).data
          : (await apiClient.post<RejectionReasonView>(`${CFG}/rejection-reasons`, body)).data,
      onSuccess: invalidate,
    }),
    /** Retires it: no longer offered, still resolving everywhere it is already named. */
    retireReason: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<RejectionReasonView>(`${CFG}/rejection-reasons/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    savePosting: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<JobPostingView>(`${CFG}/postings`, body)).data,
      onSuccess: invalidate,
    }),
    saveSetting: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<RecruitmentSettingView>(`${CFG}/settings`, body)).data,
      onSuccess: invalidate,
    }),
  }
}
